import React, { createContext, useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiRequests } from 'hooks/useApiRequests';
import { useSignalrConnection } from 'hooks';
import { useSnackbar } from 'notistack';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { getIntlLocale } from 'utils';

import { FaxStatusMessageDto, SentFaxDto, ReceivedFaxDto } from 'types/fax';

import faxDeliveredSound from 'assets/audio/fax/fax-delivered.mp3';
import faxReceivedSound from 'assets/audio/fax/fax-received.mp3';
import faxFailedSound from 'assets/audio/fax/fax-failed.mp3';
import faxQueuedSound from 'assets/audio/fax/fax-queued.mp3';

type FaxContextType = {
  faxNumber: string | null;

  sentFaxes: SentFaxDto[];
  receivedFaxes: ReceivedFaxDto[];

  loadingSent: boolean;
  loadingReceived: boolean;

  loadSentFaxes(): Promise<void>;
  loadReceivedFaxes(): Promise<void>;

  addOptimisticSentFax(fax: SentFaxDto): void;
  removeReceivedFax(id: string): void;

  scheduleSentFaxReconciliation(): void;
};

const FaxContext = createContext<FaxContextType | null>(null);

export function FaxProvider({ children }: { children: React.ReactNode }) {
  const { events } = useSignalrConnection();
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation('settings');
  const locale = getIntlLocale(i18n.resolvedLanguage || i18n.language || 'en');
  const user = useAppSelector(selectUser);

  const [faxNumber, setFaxNumber] = useState<string | null>(null);

  const [sentFaxes, setSentFaxes] = useState<SentFaxDto[]>([]);
  const [receivedFaxes, setReceivedFaxes] = useState<ReceivedFaxDto[]>([]);

  const [loadingSent, setLoadingSent] = useState(false);
  const [loadingReceived, setLoadingReceived] = useState(false);

  const addOptimisticSentFax = useCallback((fax: SentFaxDto) => {
    setSentFaxes((prev) => {
      // Prevent duplicates
      if (prev.some((f) => f.id === fax.id)) return prev;
      return [fax, ...prev];
    });
  }, []);

  const removeReceivedFax = useCallback((id: string) => {
    setReceivedFaxes((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const reloadTimeout = useRef<NodeJS.Timeout | null>(null);

  const apiRef = useRef<ReturnType<typeof useApiRequests> | null>(null);

  if (!apiRef.current) {
    apiRef.current = useApiRequests();
  }

  const { getFaxAccount, getSentFaxes, getReceivedFaxes } = apiRef.current;

  // 🔊 Audio refs
  const deliveredAudioRef = useRef<HTMLAudioElement | null>(null);
  const receivedAudioRef = useRef<HTMLAudioElement | null>(null);
  const failedAudioRef = useRef<HTMLAudioElement | null>(null);
  const queuedAudioRef = useRef<HTMLAudioElement | null>(null);

  const canReceiveFaxNotifications = useMemo(() => {
    if (!user?.clinicUser) return false;
    return user.clinicUser.canAccessFaxes === true;
  }, [user]);

  /* =====================
     Init audio
  ===================== */

  useEffect(() => {
    deliveredAudioRef.current = new Audio(faxDeliveredSound);
    receivedAudioRef.current = new Audio(faxReceivedSound);
    failedAudioRef.current = new Audio(faxFailedSound);
    queuedAudioRef.current = new Audio(faxQueuedSound);

    const unlockAudio = () => {
      [deliveredAudioRef, receivedAudioRef, failedAudioRef, queuedAudioRef].forEach((ref) => {
        if (!ref.current) return;

        ref.current.muted = false;
        ref.current
          .play()
          .then(() => {
            ref.current!.pause();
            ref.current!.currentTime = 0;
          })
          .catch((err) => {
            console.debug('Audio unlock blocked', err);
          });
      });
    };

    window.addEventListener('pointerdown', unlockAudio, { once: true });

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
    };
  }, []);

  const playFaxSound = (status: string) => {
    const s = status.toLowerCase();

    let audio: HTMLAudioElement | null = null;

    if (s.includes('receive')) audio = receivedAudioRef.current;
    else if (s.includes('queue')) audio = queuedAudioRef.current;
    else if (s.includes('fail')) audio = failedAudioRef.current;
    else if (s === 'sent' || s.includes('deliver')) audio = deliveredAudioRef.current;

    // no sound for queue events
    if (s.includes('queue')) return;

    if (!audio) return;

    try {
      audio.muted = false;
      audio.currentTime = 0;

      const playPromise = audio.play();
      if (playPromise) {
        playPromise.catch((err) => {
          console.warn('Fax sound blocked:', err);
        });
      }
    } catch (err) {
      console.warn('Fax sound error:', err);
    }
  };

  /* =====================
     Loaders
  ===================== */

  const loadFaxAccount = useCallback(async () => {
    const account = await getFaxAccount();
    setFaxNumber(account.faxNumber);
  }, [getFaxAccount]);

  const loadSentFaxes = useCallback(async () => {
    setLoadingSent(true);
    try {
      const response = await getSentFaxes();

      const mapped: SentFaxDto[] = Array.isArray(response?.result)
        ? response.result.map((fax: any) => ({
            id: fax.faxId ?? fax.FaxId ?? fax.FileName,
            fileName: fax.submittedFiles || fax.SubmittedFiles || fax.fileName || fax.FileName || '—',
            status: fax.sentStatus || fax.SentStatus || 'unknown',
            sentBy: fax.SentByName ?? fax.sentBy ?? fax.sent_by ?? 'SRFax Portal',
            sentTo: fax.toFaxNumber || fax.ToFaxNumber || '—',
            sentAt: fax.dateSent || fax.DateSent || '—',
            sentAtEpoch: Number(fax.epochTime ?? fax.EpochTime) || Date.now(),
            pages: Number(fax.pages ?? fax.Pages) || 0,
            failedMessage: fax.failedMessage || fax.FailedMessage || null,
          }))
        : [];

      setSentFaxes((prev) => {
        const byId = new Map<string, SentFaxDto>();

        // backend data first
        for (const fax of mapped) {
          byId.set(fax.id, fax);
        }

        // keep only optimistic rows that backend hasn't returned yet
        for (const fax of prev) {
          if (!byId.has(fax.id) && fax.id.startsWith('local-')) {
            byId.set(fax.id, fax);
          }
        }

        return Array.from(byId.values()).sort((a, b) => (b.sentAtEpoch ?? 0) - (a.sentAtEpoch ?? 0));
      });
    } finally {
      setLoadingSent(false);
    }
  }, [getSentFaxes]);

  const loadReceivedFaxes = useCallback(async () => {
    setLoadingReceived(true);
    try {
      const response = await getReceivedFaxes();

      const mapped: ReceivedFaxDto[] = Array.isArray(response?.result)
        ? response.result.map((fax: any) => {
            const receivedDate = new Date(fax.receivedAt);

            return {
              id: fax.faxId, // stable, unique
              fileName: fax.fileName,

              // ✅ FIX: correct source
              from: fax.from || 'Unknown',

              // ✅ FIX: formatted exactly like Sent Faxes
              receivedAt: receivedDate.toLocaleString(locale, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              }),

              // ✅ keep epoch for sorting
              receivedAtEpoch: receivedDate.getTime(),

              pages: Number(fax.pages),
              sizeBytes: Number(fax.sizeBytes),
              viewed: Boolean(fax.viewed),
            };
          })
        : [];

      setReceivedFaxes(mapped);
    } finally {
      setLoadingReceived(false);
    }
  }, [getReceivedFaxes, locale]);

  const debouncedReloadSent = useCallback(() => {
    if (reloadTimeout.current) {
      clearTimeout(reloadTimeout.current);
    }

    reloadTimeout.current = setTimeout(() => {
      loadSentFaxes();
    }, 750);
  }, [loadSentFaxes]);

  const debouncedReloadReceived = useRef<NodeJS.Timeout | null>(null);

  const reloadReceived = () => {
    if (debouncedReloadReceived.current) {
      clearTimeout(debouncedReloadReceived.current);
    }

    debouncedReloadReceived.current = setTimeout(() => {
      loadReceivedFaxes();
    }, 500);
  };

  const scheduleSentFaxReconciliation = useCallback(() => {
    setTimeout(async () => {
      // Only bother if optimistic rows still exist
      setSentFaxes((prev) => {
        const hasOptimistic = prev.some((f) => f.id.startsWith('local-'));
        if (!hasOptimistic) return prev;

        // Trigger a real reload
        loadSentFaxes();
        return prev;
      });
    }, 60_000); // 1 minute
  }, [loadSentFaxes]);

  /* =====================
     Initial load
  ===================== */

  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    loadFaxAccount();
    loadSentFaxes();
    loadReceivedFaxes();
  }, []);

  /* =====================
     SignalR listener
  ===================== */

  useEffect(() => {
    if (!events) return;

    const handler = (message: FaxStatusMessageDto) => {
      const statusLower = message.status.toLowerCase();

      // 🔔 Always notify user
      if (canReceiveFaxNotifications) {
        enqueueSnackbar(
          statusLower === 'failed'
            ? t('faxNotifications.failed')
            : statusLower === 'sent' || statusLower === 'delivered'
            ? t('faxNotifications.delivered')
            : t('faxNotifications.status', { status: message.status }),
          {
            variant:
              statusLower === 'failed'
                ? 'error'
                : statusLower === 'sent' || statusLower === 'delivered'
                ? 'success'
                : 'info',
          },
        );

        playFaxSound(message.status);
      }

      // 🧹 Remove optimistic rows once SRFax confirms delivery
      if (statusLower === 'sent' || statusLower === 'delivered') {
        setSentFaxes((prev) => prev.filter((f) => !f.id.startsWith('local-')));

        // 🔄 Force authoritative refresh
        debouncedReloadSent();
      }

      // 🔁 Best-effort in-place update if ID happens to match
      setSentFaxes((prev) =>
        prev.map((fax) => (fax.id === message.srFaxId ? { ...fax, status: message.status } : fax)),
      );
    };

    events.on('faxStatusUpdated', handler);

    return () => {
      events.off('faxStatusUpdated', handler);
    };
  }, [canReceiveFaxNotifications, debouncedReloadSent, enqueueSnackbar, events, t]);

  useEffect(() => {
    if (!events || !canReceiveFaxNotifications) return;

    const handler = () => {
      enqueueSnackbar(t('faxNotifications.received'), { variant: 'info' });

      // optional but recommended
      playFaxSound('received');

      reloadReceived();
    };

    events.on('faxReceived', handler);

    return () => {
      events.off('faxReceived', handler);
    };
  }, [canReceiveFaxNotifications, enqueueSnackbar, events, t]);

  /* =====================
     Context value
  ===================== */

  const value: FaxContextType = {
    faxNumber,
    sentFaxes,
    receivedFaxes,
    loadingSent,
    loadingReceived,
    loadSentFaxes,
    loadReceivedFaxes,
    addOptimisticSentFax,
    removeReceivedFax,
    scheduleSentFaxReconciliation,
  };

  return <FaxContext.Provider value={value}>{children}</FaxContext.Provider>;
}

export function useFax() {
  const ctx = useContext(FaxContext);
  if (!ctx) {
    throw new Error('useFax must be used within FaxProvider');
  }
  return ctx;
}
